import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { Briefcase, MapPin, Calendar, ExternalLink, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTRACT_COLORS = {
  CDI: 'bg-[#4A6B48]/10 text-[#4A6B48]',
  CDD: 'bg-blue-100 text-blue-700',
  Stage: 'bg-purple-100 text-purple-700',
  Bénévolat: 'bg-orange-100 text-orange-700',
  Consultance: 'bg-pink-100 text-pink-700',
  "Bourse d'études": 'bg-amber-100 text-amber-800',
};

// Map URL ?category=... to contract_type values for sub-tab filtering
const CATEGORY_FILTERS = {
  volontariat: { contracts: ['Bénévolat'], title: 'Volontariat', subtitle: 'Engagez-vous comme bénévole auprès de CRESA ASBL.' },
  stage: { contracts: ['Stage'], title: 'Stages', subtitle: 'Découvrez nos opportunités de stages.' },
  bourse: { contracts: ["Bourse d'études"], title: "Bourses d'études", subtitle: "Bourses d'études et de formation accessibles via CRESA." },
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const categoryFilter = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const cat = new URLSearchParams(window.location.search).get('category');
    return cat ? CATEGORY_FILTERS[cat] || null : null;
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const visibleJobs = useMemo(() => {
    if (!categoryFilter) return jobs;
    return jobs.filter((j) => categoryFilter.contracts.includes(j.contract_type));
  }, [jobs, categoryFilter]);

  const isExpired = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="jobs-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <Briefcase className="text-[#4A6B48]" size={40} />
            </div>
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="jobs-title"
            >
              {categoryFilter ? categoryFilter.title : 'Recrutement'}
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              {categoryFilter ? categoryFilter.subtitle : (
                <>
                  Rejoignez l&apos;équipe CRESA ASBL et participez à la transformation des îlots de développement
                  intégral en RD Congo. Consultez nos offres et celles de nos partenaires ci-dessous —
                  <strong> emplois, stages, bénévolats et bourses d&apos;études</strong> dans la sous-région des
                  Grands Lacs et à l&apos;international.
                </>
              )}
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}

          {!loading && visibleJobs.length === 0 && (
            <div className="text-center py-12" data-testid="no-jobs-message">
              <p className="text-[#5C665A]">
                {categoryFilter
                  ? `Aucune offre de type "${categoryFilter.title}" disponible pour le moment.`
                  : 'Aucune offre disponible pour le moment. Revenez bientôt !'}
              </p>
            </div>
          )}

          {!loading && visibleJobs.length > 0 && (
            <div className="space-y-6 max-w-4xl mx-auto">
              {visibleJobs.map((job) => {
                const expired = isExpired(job.deadline);
                return (
                  <article
                    key={job.job_id}
                    className={`card ${expired ? 'opacity-60' : ''}`}
                    data-testid={`job-card-${job.job_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${CONTRACT_COLORS[job.contract_type] || 'bg-gray-100 text-gray-700'}`}>
                          {job.contract_type}
                        </span>
                        {job.region && (
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700">
                            🌍 {job.region}
                          </span>
                        )}
                        <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                          <MapPin size={12} /> {job.location}
                        </span>
                        <span className={`text-xs inline-flex items-center gap-1 ${expired ? 'text-red-600 font-bold' : 'text-[#5C665A]'}`}>
                          <Calendar size={12} />
                          {expired ? 'Date limite dépassée' : `Postuler avant le ${new Date(job.deadline).toLocaleDateString('fr-FR')}`}
                        </span>
                      </div>
                    </div>

                    <h2
                      className="text-2xl font-bold text-[#2E332D] mb-3"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {job.title}
                    </h2>

                    <div className="mb-4">
                      <h3 className="text-sm font-bold text-[#4A6B48] mb-1 flex items-center gap-1">
                        <FileText size={14} /> Description du poste
                      </h3>
                      <p className="text-sm text-[#5C665A] leading-relaxed whitespace-pre-wrap">{job.description}</p>
                    </div>

                    <div className="mb-5">
                      <h3 className="text-sm font-bold text-[#4A6B48] mb-1">Profil recherché</h3>
                      <p className="text-sm text-[#5C665A] leading-relaxed whitespace-pre-wrap">{job.profile}</p>
                    </div>

                    {!expired && job.google_form_url && (
                      <a
                        href={job.google_form_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#D87D4A] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#C66B39] transition-colors"
                        data-testid={`apply-job-${job.job_id}`}
                      >
                        Postuler maintenant
                        <ExternalLink size={16} />
                      </a>
                    )}
                    {expired && (
                      <p className="text-sm font-bold text-red-600">Cette offre est clôturée.</p>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
